export type Nullable<T> = {
  [P in keyof T]: T[P] | null;
};

export function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function isNumberArray(value: any): value is number[] {
  return (
    Array.isArray(value) && value.every((item) => typeof item === "number")
  );
}

export function isStringArray(value: any): value is string[] {
  return (
    Array.isArray(value) && value.every((item) => typeof item === "string")
  );
}

export function isString(value: any): value is string {
  return typeof value === "string";
}

export function isNumber(value: any): value is number {
  return typeof value === "number";
}

export type PartialExcept<T, K extends keyof T> = Pick<T, K> &
  Partial<Omit<T, K>>;

export type Paths<T extends object> = {
  [K in keyof T]: `${Exclude<K, symbol>}${T[K] extends object
    ? `.${Paths<T[K]>}`
    : ""}`;
}[keyof T];

type ReplaceFistTuple<T extends readonly any[], TReplace> = {
  [K in keyof T]: K extends "0" ? TReplace : T[K];
};

export type ReplaceFistParam<F, ReplaceT> = F extends (
  ...args: infer ParamT
) => infer R
  ? (...args: ReplaceFistTuple<ParamT, ReplaceT>) => R
  : never;

export function getTime(
  timeString: string
): { hour: number; minute: number } | null {
  const array = timeString.split(":");
  if (array.length != 2) {
    return null;
  }
  const hour = parseInt(array[0]);
  const minute = parseInt(array[1]);
  if (isNaN(hour) || isNaN(minute)) {
    return null;
  }
  return { hour, minute };
}

export function isArrayEqual<T>(
  a: T[],
  b: T[],
  equalComparer?: (a: T, b: T) => boolean
): boolean {
  if (a.length != b.length) {
    return false;
  }
  let i = a.length;
  while (i-- >= 0) {
    if (equalComparer == null ? a[i] != b[i] : !equalComparer(a[i], b[i])) {
      return false;
    }
  }
  return true;
}

export type ObjectInterSection<T1, T2> = T1 extends object
  ? T2 extends object
    ? {
        [P in keyof T1 & keyof T2]: ObjectInterSection<T1[P], T2[P]>;
      }
    : never
  : T1 extends T2
  ? T2 extends T1
    ? T1
    : never
  : never;

export function removeUndefined<T extends Record<string, any>>(obj: T): T {
  Object.keys(obj).forEach((key: any) =>
    obj[key] === undefined ? delete obj[key] : {}
  );
  return obj;
}